<template>
  <div class="overflow-hidden card h-100">
    <div class="pt-3 pb-0 pe-0 card-body">
      <div class="row">
        <div class="col-sm-6">
          <div class="d-flex">
            <div class="ms-3">
              <div class="mb-0 text-sm text-uppercase text-primary font-weight-bold">
                {{ dateRange }}
              </div>
              <h5 id="header" class="mb-0 font-weight-bolder mt-1">
                {{ title }} {{ this.lastSynInfo }}
              </h5>
            </div>
          </div>
        </div>
        <div class="col-sm-6 d-flex justify-content-end mt-3">
          <div class="btn-group me-4" role="group" data-toggle="buttons">
            <label
              v-for="(option, index) in dayOptions"
              :id="index"
              :key="option.name"
              class="btn btn-outline-white"
              :class="{ active: activeIndex === index }"
            >
              <input
                type="radio"
                name="options"
                class="btn-check"
                autocomplete="off"
                :checked="activeIndex === index"
                @click="initChart(index)"
              />
              <span class="d-none d-sm-block p-1">{{ option.name }}</span>
            </label>
          </div>
        </div>

        <div class="col-12"> 
          <bed-wake-time-range-chart v-if="bedAndWakeTimeData.length > 0" 
             :sleep-points="bedAndWakeTimeData"/>
        </div>

        <div class="col-12">
          <user-sleep-chart v-if="chart"
            id="user-sleep-chart" title="" height="400" :chart="chart" />
        </div>

        <div class="col-12">    
          <sleep-trends-chart title="Sleep Hours Vs Needed" :date-range="dateRange" 
          :last-syn-info="lastSynInfo" :sleep-features="sleepFeatures" />
        </div>

        <div class="col-12">
          <sleep-debt-line-chart :date-range="dateRange" 
            :sleep-features="sleepFeatures" /> 
        </div>

        <div class="row bg-dark py-3">
          <div class="col-12 m-0">
            <div class="table-responsive pt-2 px-3">
              <el-table ref="dataTable"
                :data="userSleepFilteredData"
                style="width: 100%"
                @sort-change="sortColumn"
                @row-click="handleSleepRowClick"
              >
                <el-table-column
                  v-for="column in columns"
                  :key="column.prop"
                  :prop="column.prop"
                  :label="column.label"
                  :formatter="column.formatter"
                  :min-width="column.minWidth"
                  :width="column.width"
                  :class-name="column.class"
                  sortable="custom"
                  label-class-name="text-capitalize"
                >
                  <template
                    v-if="column.prop == 'sleep_score'"
                    #default="scope"
                  >
                    <argon-progress
                      :color="getSleepScoreColor(scope.row.sleep_score)"
                      :percentage="scope.row.sleep_score"
                      :left-text="`${scope.row.sleep_score}`"
                    />
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="card-footer paginator">
      <div
        v-if="userSleepData.length > 0"
        class="d-flex justify-content-between align-items-center"
      >
        <span class="ms-3 text-muted"
          >{{ userSleepData.length }} record(s)</span
        >
        <app-pagination
          :records="userSleepData.length"
          :page-size="pageSize"
          :page="page"
          @update-page-selection="
            (selectedValue) => updatePageSelection(selectedValue)
          "
        />
      </div>
    </div>
  </div>

  <!-- Sleep details Modal -->
  <modal
    id="sleepDetailsModal"
    v-model:show="sleepDetailsModal"
    class="modal fade"
    footer-classes="justify-content-end align-items-center"
    :show-close="true"
    :modalContentClasses="'sleep-intense-model'" >
    <template #header>
      <div class="header-container">
        <div class="header-text text-xl text-capitalize">Sleep Details</div>
        </div>
    </template>

    <div class="p-2">
      <SleepSummary  v-if="this.selectedSleepRow" :selected-date="this.selectedSleepRow.bucket"
         :udid="udid"  :sleep-data="this.selectedSleepRow" />
    </div>

    <template #footer>
      <button type="button" class="btn btn-primary"
        @click="closeModal">DONE</button>
    </template>
  </modal>
</template>

<script>
const dayjs = require("dayjs");
import Constants from "@/helpers/constants.js";
import Utils from "@/helpers/utils";
import Modal from "@/shared/Modal.vue";
import ArgonProgress from "@/shared/Controls/ArgonProgress.vue";
import UserSleepChart from "../components/UserSleepChart.vue";
import BedWakeTimeRangeChart from "./sleep/BedWakeTimeRangeChart.vue";
import SleepSummary from "./insights/sleep-details/SleepSummary.vue";
import SleepTrendsChart from "./sleep/SleepTrendsChart.vue";
import SleepDebtLineChart from "./sleep/SleepDebtLineChart.vue";
import styles from "./UserSleep.vue?vue&type=style&index=0&lang=scss&module=1";

export default {
  name: "UserSleep",
  components: {
    ArgonProgress,
    UserSleepChart,
    Modal,
    SleepSummary,
    SleepTrendsChart,
    BedWakeTimeRangeChart,
    SleepDebtLineChart,
  },
  props: {
    title: {
      type: String,
      default: "Sleep",
    },
    udid: {
      type: String,
      required: true,
    },
    selectedDate: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      columns: [
        {
          prop: "status_start_time",
          label: "Bed Time",
          formatter: (row) =>
            `${this.formatStatusStartTime(row.status_start_time)}`,
        },
        {
          prop: "status_end_time",
          label: "Wake Time",
          formatter: (row) =>
            `${this.formatStatusStartTime(row.status_end_time)}`,
        },
        {
          prop: "sleep_minutes",
          label: "Hours of Sleep",
          formatter: (row) =>
            `${this.formatMinutes(row.sleep_minutes - row.awake_minutes)}`,
        },
        {
          prop: "restorative_minutes",
          label: "Restorative Hours",
          formatter: (row) => `${this.formatMinutes(row.restorative_minutes)}`,
          width: 180,
        },
        {
          prop: "wake_events",
          label: "Wake Events",
        },
        {
          prop: "deep_sleep_part_count",
          label: "Deep Sleep Continuity",
          formatter: (row) => `${row.deep_sleep_part_count} pts`,
          width: 200,
        },
        {
          prop: "breathing_quality",
          label: "Breathing Quality",
          formatter: (row) => `${row.breathing_quality} pts`,
          width: 180,
        },
        {
          prop: "sleep_score",
          label: "Score",
          width: 220,
        },
      ],
      page: Constants.DEFAULT_PAGE,
      pageSize: Constants.DEFAULT_PAGE_SIZE,
      sort: "bucket",
      order: "asc",
      activeIndex: 1,
      options: [30, 14, 7],
      chart: null,
      userSleepData: [],
      userSleepFilteredData: [],
      lastSyncDate:'',
      lastSynInfo: '',
      selectedSleepRow: null,
      sleepDetailsModal: false,
      bedAndWakeTimeData: [],
      sleepFeatures: [],
    };
  },
  computed: {
    dayOptions() {
      let values = [];
      this.options.forEach((opt) => {
        values.push({ name: `${opt} days` });
      });
      return values;
    },
    dateRange() {
      let maxDate = dayjs(this.selectedDate);
      let daysCount = this.options[this.activeIndex];

      return `Last ${daysCount} days : ${maxDate
        .subtract(daysCount - 1, "days")
        .format(Constants.CHART_DATE_FORMAT)} to ${maxDate.format(
        Constants.CHART_DATE_FORMAT
      )}`;
    },
  },
  watch: {
    udid() {
      this.initChart(this.activeIndex);
    },
    selectedDate() {
      this.getDataForPeriod();
    },
  },
  mounted() {
    if (this.udid) this.initChart(this.activeIndex);
  },

  methods: {
    sortColumn({ prop, order }) {
      // since all columns are numbers, we use value sort
      // if we add columns with string data in future, add a check and implement string sort
      if (order == "ascending") {
        this.userSleepData.sort((a, b) => a[prop] - b[prop]);
      } else {
        this.userSleepData.sort((a, b) => b[prop] - a[prop]);
      }

      this.updatePageSelection(this.page);
    },
    formatDate(dt) {
      return dayjs(dt).format(Constants.TABLE_DATE_FORMAT);
    },
    formatStatusStartTime(ts) {
      if (isNaN(parseInt(ts))) return "-";
      return dayjs(parseInt(ts)).format(
        Constants.TABLE_DAY_OF_WEEK_DATETIME_FORMAT
      );
    },
    formatDateTime(ts) {
      if (isNaN(parseInt(ts))) return "-";
      return dayjs(parseInt(ts)).format(Constants.TABLE_DATETIME_FORMAT);
    },
    formatMinutes(minutes) {
      const durationMs = minutes * 60 * 1000;
      return dayjs
        .duration(durationMs)
        .format(Constants.TABLE_HOURS_MINUTES_FORMAT);
    },
    getSleepScoreColor(score) {
      return Utils.getSleepScoreColor(score);
    },
    initChart(index) {
      this.activeIndex = index;
      this.getDataForPeriod();
    },
    getDataForPeriod() {
      let daysCount = this.options[this.activeIndex];

      this.$store
        .dispatch("data/userSleepData", {
          udid: this.udid,
          sort: this.sort,
          order: this.order,
          dt_from: dayjs(this.selectedDate)
            .startOf("day")
            .subtract(daysCount - 1, "day")
            .format(Constants.BUCKET_FORMAT),
          dt_to: dayjs(this.selectedDate)
            .startOf("day")
            .format(Constants.BUCKET_FORMAT),
        })
        .then((results) => {
          this.userSleepData = results.data.sleepData || [];
          this.sleepFeatures = results.data.sleepFeatures || [];
          this.lastSyncDate = dayjs(results.lastSyncDate).format(Constants.TABLE_DOB_FORMAT);
          if(dayjs(this.selectedDate).isAfter(dayjs(this.lastSyncDate))) {
            this.lastSynInfo = '- (Last Sync Date: '+this.lastSyncDate+')';
          } else {
            this.lastSynInfo = '';
          }
          this.updatePageSelection(Constants.DEFAULT_PAGE);
          let chartDays = [];
          let sleep = {
            scores: [],
            lightSleep: [],
            remSleep: [],
            deepSleep: [],
            awake: [],
            startTimes: [],
            endTimes: [],
          };

          this.bedAndWakeTimeData = [];
          this.userSleepData.map((record) => {
            sleep.scores.push(record.sleep_score);
            sleep.lightSleep.push(record.light_sleep_minutes);
            sleep.remSleep.push(record.rem_sleep_minutes);
            sleep.deepSleep.push(record.deep_sleep_minutes);
            sleep.awake.push(record.awake_minutes);
            let chartDay = [
              dayjs(record.bucket).format(Constants.CHART_DATE_FORMAT),
              dayjs(record.bucket).format(Constants.CHART_DATE_DAY_OF_WEEK_FORMAT),
            ];
            chartDays.push(chartDay);
            this.bedAndWakeTimeData.push({
              sleepDate: dayjs(record.bucket).format(Constants.CHART_DATE_FORMAT),
              sleepDay: dayjs(record.bucket).format(Constants.CHART_DATE_DAY_OF_WEEK_FORMAT),
              bedTime: Utils.formatToHHMM_UTC(record.status_start_time),
              wakeTime: Utils.formatToHHMM_UTC(record.status_end_time),
            });
          });
          this.chart = {
            labels: chartDays,
            datasets: [
              {
                label: "REM Sleep",
                data: sleep.remSleep,
                backgroundColor: styles.colorRemSleep,
              },
              {
                label: "Deep Sleep",
                data: sleep.deepSleep,
                backgroundColor: styles.colorDeepSleep,
              },
              {
                label: "Light Sleep",
                data: sleep.lightSleep,
                backgroundColor: styles.colorLightSleep,
              },
              {
                label: "Awake",
                data: sleep.awake,
                backgroundColor: styles.colorAwake,
              },
              {
                label: "Sleep Score",
                data: sleep.scores,
              },
            ],
          };
        });
    },
    // we use client side pagination here to sync with the sleep chart data
    updatePageSelection(selectedPage) {
      this.page = selectedPage;
      let reversedData = Object.assign([], this.userSleepData).reverse();
      this.userSleepFilteredData = reversedData.slice(
        this.pageSize * this.page - this.pageSize,
        this.pageSize * this.page
      );
    },  
    handleSleepRowClick(sleepRow) {
      this.selectedSleepRow = null;
      this.sleepDetailsModal = !this.sleepDetailsModal;
      this.selectedSleepRow = sleepRow;
    },
    closeModal() {
        this.selectedWorkoutRow = null;
        this.sleepDetailsModal = false;
    },
  },
};
</script>
<style lang="scss" scoped>
:export {
  colorLightSleep: $color-sleep-light;
  colorRemSleep: $color-sleep-rem;
  colorDeepSleep: $color-sleep-deep;
  colorAwake: $color-sleep-awake;
}

.btn-outline-white {
  background: none;
}
label {
  &.active {
    color: $g-gray !important;
  }
}
.card .form-select {
  background-color: $color-background-info-pane;
}
div .bg-dark h5 {
  color: $lighter !important;
}
</style>
